import { KeyboardEvent } from 'react';

import { observer } from 'mobx-react-lite';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { IconSSO } from '@components/icons/sso';
import { PasswordField } from '@components/password-field';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import { UnauthedPage } from 'components/unauthed-page';
import { Modals, useModal } from 'features/modals';

export type SigninErrors = {
  email?: string;
  password?: string;
  nonFieldErrors?: string;
};

export type SigninProps = {
  email: string;
  password: string;
  onChange: (field: string, val: string) => void;
  onSubmit: () => void;
  disableSubmit: boolean;
  errors: SigninErrors;
};

export const Signin = observer((props: SigninProps) => {
  const { email, password, onChange, onSubmit, disableSubmit, errors } = props;

  const { t } = useScopedTranslate(locale);
  const { showModal } = useModal();

  function handleKeyPress(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      onSubmit();
    }
  }

  function ssoModal() {
    // @ts-ignore SSO_SIGNIN modal is not properly typed
    showModal('SSO_SIGNIN', { email });
  }
  return (
    <UnauthedPage>
      <Modals />
      <Stack space={4}>
        <Stack orientation="horizontal" justify="center">
          <Text font="heading1" text={t('heading')} />
        </Stack>
        <Stack space={4}>
          <Button
            iconLeft={<IconSSO size="24" />}
            isFullWidth
            onClick={ssoModal}
            text={t('signinMicrosoftEnterprise')}
            variant="secondary"
          />
        </Stack>
        <Stack space={2} orientation="horizontal" justify="center" align="center">
          <Separator />
          <Text font="semibold14" color="textSecondary" text={t('or')} />
          <Separator />
        </Stack>
        <Stack space={4}>
          <TextField
            placeholder={t('emailPlaceholder')}
            label={t('core.email')}
            autoFocus
            error={errors.email}
            value={email}
            onChange={(val) => onChange('email', val)}
            isDisabled={disableSubmit}
            onKeyPress={handleKeyPress}
          />

          <PasswordField
            placeholder={t('passwordPlaceholder')}
            label={t('password')}
            error={errors.password || errors.nonFieldErrors}
            value={password}
            onChange={(val) => onChange('password', val)}
            isDisabled={disableSubmit}
            onKeyPress={handleKeyPress}
          />

          <Button
            isDisabled={disableSubmit}
            onClick={onSubmit}
            text={t('signin')}
            variant="primary-blue"
            dataTestid="signin-button"
          />
        </Stack>
        <Stack space={2} align="center">
          <ActionText.Link
            to="/forgotpassword"
            children={t('forgotPassword')}
            font="regular14"
            color="purple60"
          />
        </Stack>
      </Stack>
    </UnauthedPage>
  );
});

const locale = {
  en: {
    heading: 'Welcome back!',
    password: 'Password',
    signin: 'Sign in',
    emailPlaceholder: 'Enter your email...',
    passwordPlaceholder: 'Enter password...',
    signinMicrosoftEnterprise: 'Sign in with SSO',
    forgotPassword: 'Forgot password?',
    or: 'OR',
  },
  is: {
    heading: 'Velkomi/n/ð/nn aftur!',
    password: 'Lykilorð',
    signin: 'Skrá inn',
    emailPlaceholder: 'Sláðu inn netfangið þitt...',
    passwordPlaceholder: 'Sláðu inn lykilorðið...',
    signinMicrosoftEnterprise: 'Skrá inn með SSO',
    forgotPassword: 'Gleymdirðu lykilorðinu þínu?',
    or: 'EÐA',
  },
};
